<template>

  <v-dialog v-model="joblogsdialog" max-width="960px" scrollable persistent>

    <v-card>

      <v-card-title class="d-flex align-center">

        <span class="text-h6">Job logs</span>

        <v-chip

          v-if="liveTail"

          x-small

          color="green"

          text-color="white"

          class="ml-2 live-chip"

        >

          <v-icon x-small left>mdi-circle</v-icon>

          Live

        </v-chip>

        <v-spacer />

        <v-chip v-if="jobId" small outlined class="mr-2">{{ jobId }}</v-chip>

        <v-btn icon @click="refresh" :loading="loading" :disabled="!jobId">

          <v-icon>mdi-refresh</v-icon>

        </v-btn>

      </v-card-title>



      <v-divider />



      <v-card-text ref="logScroll" class="pa-0 job-log-scroll" style="max-height: 70vh">

        <v-progress-linear v-if="loading" indeterminate color="primary" />



        <div v-else-if="!items.length" class="pa-8 text-center text--secondary">

          No log entries for this job yet.

          <div v-if="liveTail" class="text-caption mt-2">Waiting for live output…</div>

        </div>



        <div v-else class="job-log-list">

          <div

            v-for="(row, index) in items"

            :key="row.Id || `row-${index}`"

            class="job-log-row"

            :class="rowClass(row.Status)"

          >

            <div class="job-log-meta">

              <v-chip x-small dark :color="typeColor(row.Status)" class="mr-2">

                {{ typeLabel(row.Status) }}

              </v-chip>

              <span class="job-log-time">{{ row.createdAt }}</span>

            </div>

            <pre class="job-log-text">{{ row.JobLog }}</pre>

          </div>

        </div>

      </v-card-text>



      <v-divider />



      <v-card-actions>

        <span v-if="items.length" class="text-caption text--secondary pl-2">

          {{ items.length }} entries

        </span>

        <v-spacer />

        <v-btn text color="primary" @click="joblogsdialog = false">Close</v-btn>

      </v-card-actions>

    </v-card>

  </v-dialog>

</template>



<style scoped>

.job-log-scroll {

  overflow-y: auto;

}



.job-log-list {

  font-family: Consolas, "Courier New", monospace;

  font-size: 13px;

}



.job-log-row {

  border-bottom: 1px solid rgba(0, 0, 0, 0.08);

  padding: 10px 16px;

}



.job-log-row:last-child {

  border-bottom: none;

}



.job-log-meta {

  display: flex;

  align-items: center;

  margin-bottom: 6px;

}



.job-log-time {

  font-size: 11px;

  color: rgba(0, 0, 0, 0.54);

  font-family: Roboto, sans-serif;

}



.job-log-text {

  margin: 0;

  white-space: pre-wrap;

  word-break: break-word;

  font-family: inherit;

  font-size: inherit;

  line-height: 1.45;

  background: transparent;

}



.job-log-row--stdout {

  background-color: #fafafa;

}



.job-log-row--stderr {

  background-color: #fff3f0;

}



.job-log-row--system {

  background-color: #f3f8ff;

}



.job-log-row--success {

  background-color: #edf7ed;

}



.live-chip {

  animation: live-pulse 1.6s ease-in-out infinite;

}



@keyframes live-pulse {

  0%,

  100% {

    opacity: 1;

  }

  50% {

    opacity: 0.65;

  }

}

</style>



<script>

import Api from "../api";

import socket from "../socket";

import { timeAwareFormat } from "../time";

import { OPEN_JOB_LOG_TABLE_DIALOG, CLOSE_JOB_LOG_TABLE_DIALOG } from "../store/modules/job";

import { mapActions } from "vuex";



export default {

  computed: {

    joblogsdialog: {

      get() {

        return this.$store.state.job.openLogTableDialog;

      },

      set(val) {

        if (val) {

          this.openLogDialog();

        } else {

          this.closeLogDialog();

        }

      },

    },

    jobId() {

      return this.$store.state.job.jobid;

    },

    liveTail() {

      return this.joblogsdialog && !!this.jobId;

    },

  },

  watch: {

    joblogsdialog(open) {

      if (open && this.jobId) {

        this.refresh();

        this.bindLiveTail();

      }

      if (!open) {

        this.items = [];

        this.unbindLiveTail();

      }

    },

    jobId(id) {

      if (this.joblogsdialog && id) {

        this.refresh();

      }

    },

  },

  beforeDestroy() {

    this.unbindLiveTail();

  },

  methods: {

    ...mapActions("job", {

      openLogDialog: OPEN_JOB_LOG_TABLE_DIALOG,

      closeLogDialog: CLOSE_JOB_LOG_TABLE_DIALOG,

    }),

    bindLiveTail() {

      if (this._liveTailBound) {

        return;

      }

      this._onJobLogAppend = (payload) => this.handleLiveAppend(payload);

      socket.on("job_log_append", this._onJobLogAppend);

      this._liveTailBound = true;

    },

    unbindLiveTail() {

      if (!this._liveTailBound || !this._onJobLogAppend) {

        return;

      }

      socket.off("job_log_append", this._onJobLogAppend);

      this._liveTailBound = false;

      this._onJobLogAppend = null;

    },

    handleLiveAppend(payload) {

      if (!this.liveTail || !payload || payload.jobId !== this.jobId) {

        return;

      }

      this.appendEntry(payload.entry, { scroll: true });

    },

    typeLabel(status) {

      switch (status) {

        case "STDOUT":

          return "Output";

        case "STDERR":

          return "Error";

        case "Queue":

          return "Queue";

        case "Start":

          return "Start";

        case "Finish":

          return "Finish";

        case "Reconnect":

          return "Reconnect";

        case "Disconnect":

          return "Disconnect";

        default:

          return status || "Log";

      }

    },

    typeColor(status) {

      switch (status) {

        case "STDOUT":

          return "blue-grey";

        case "STDERR":

          return "red darken-1";

        case "Queue":

          return "deep-purple";

        case "Start":

          return "amber darken-2";

        case "Finish":

          return "green darken-1";

        case "Reconnect":

          return "cyan darken-1";

        case "Disconnect":

          return "orange darken-2";

        default:

          return "grey";

      }

    },

    rowClass(status) {
      if (status === "STDOUT") return "job-log-row--stdout";
      if (status === "STDERR") return "job-log-row--stderr";
      if (status === "Finish") return "job-log-row--success";
      return "job-log-row--system";
    },

    transformRow(row) {

      return {

        ...row,

        createdAt: row.createdAt ? timeAwareFormat(row.createdAt) : timeAwareFormat(new Date()),

        JobLog: row.JobLog != null ? String(row.JobLog) : "",

      };

    },

    transformRows(rows) {

      return (rows || []).map((row) => this.transformRow(row));

    },

    appendEntry(entry, { scroll = false } = {}) {

      if (!entry) {

        return;

      }



      const id = entry.Id;

      if (id && this.items.some((row) => row.Id === id)) {

        return;

      }



      this.items.push(this.transformRow(entry));

      this.totalCount = this.items.length;



      if (scroll) {

        this.scrollToBottom();

      }

    },

    scrollToBottom() {

      this.$nextTick(() => {

        const el = this.$refs.logScroll?.$el || this.$refs.logScroll;

        if (el) {

          el.scrollTop = el.scrollHeight;

        }

      });

    },

    refresh() {

      if (!this.jobId) {

        this.items = [];

        return;

      }



      this.loading = true;

      Api.listJobLogs(this.jobId)

        .then(({ rows, count }) => {

          this.items = this.transformRows(rows);

          this.totalCount = count;

          this.scrollToBottom();

        })

        .catch(() => {

          this.items = [];

          this.totalCount = 0;

        })

        .finally(() => {

          this.loading = false;

        });

    },

  },

  data: () => ({

    items: [],

    totalCount: 0,

    loading: false,

    _liveTailBound: false,

    _onJobLogAppend: null,

  }),

};

</script>


